import {
  formatDate,
  getCleanedUrlParams,
  sortArray,
  timeFromNow,
} from '@utils';
import {
  BOARDS_ID,
  FILTER_OPERATOR_ENUMS,
  FILTER_RULE_ENUMS,
} from '@constants';

import {
  LOGISTICS_DATE_FORMAT,
  LOGISTICS_FIELDS_MAPPER,
  LOGISTICS_DEFAULT_ORDER,
  LOGISTICS_DEFAULT_ORDER_BY,
  LOGISTICS_PAGE_SIZE,
  LOGISTICS_SORTING_KEYS,
  LOGISTICS_FILTERS_KEYS,
} from 'components/logistics/constants';

const getFieldValueByRuleType = (field, value) => {
  switch (field) {
    case LOGISTICS_FIELDS_MAPPER.category.key:
    case LOGISTICS_FIELDS_MAPPER.owner.key:
    case LOGISTICS_FIELDS_MAPPER.status.key:
      return value.split(',').map((item) => Number(item));
    case LOGISTICS_FIELDS_MAPPER.primaryDriver.key:
    case LOGISTICS_FIELDS_MAPPER.secondaryDriver.key:
      return value.split(',').map((v) => (v === 'None' ? '' : v));
    default:
      return Number.isNaN(Number(value))
        ? value.split(',')
        : parseInt(value, 10);
  }
};

const handleLogisticsData = ({
  data = [],
  servicePoints = [],
  currentLocations = [],
}) => {
  if (!data) {
    return [];
  }
  const servicePointsMap = new Map(
    servicePoints.map(({ value, ...rest }) => [value, rest])
  );
  const currentLocationsMap = new Map(
    currentLocations.map(({ value, ...rest }) => [value, rest])
  );

  return data?.map((item) => {
    const { customFields, ...rest } = item;
    const deliveryVehicle =
      item?.customFields?.deliveryVehicleBrand &&
      item?.customFields?.deliveryVehicleModel
        ? `${item?.customFields?.deliveryVehicleBrand} ${item?.customFields?.deliveryVehicleModel}`
        : '-';
    const returnVehicle =
      item?.customFields?.returnVehicleBrand &&
      item?.customFields?.returnVehicleModel
        ? `${item?.customFields?.returnVehicleBrand} ${item?.customFields?.returnVehicleModel}`
        : '-';

    return {
      ...rest,
      ...customFields,
      category:
        LOGISTICS_FIELDS_MAPPER.category.options.find(
          (option) => option.value === item?.customFields?.category
        )?.text || '-',
      owner:
        LOGISTICS_FIELDS_MAPPER.owner.options.find(
          (option) => option.value === item?.customFields?.owner
        )?.text || '-',
      servicePoint:
        LOGISTICS_FIELDS_MAPPER.servicePoint.options.find(
          (option) => option.value === item?.customFields?.servicePoint
        )?.text || '-',
      status: item?.status || '-',
      primaryDriver: item?.customFields?.primaryDriver || '-',
      secondaryDriver: item?.customFields?.secondaryDriver || '-',
      pickupLocation:
        servicePointsMap.get(item?.customFields?.pickupLocation)?.text || '-',
      dropoffLocation:
        servicePointsMap.get(item?.customFields?.dropoffLocation)?.text || '-',
      currentLocation:
        currentLocationsMap.get(item?.customFields?.currentLocation)?.text ||
        '-',
      createdAt: item?.createdAt
        ? formatDate(item?.createdAt, LOGISTICS_DATE_FORMAT)
        : '-',
      scheduledDate: item?.customFields?.scheduledDate
        ? formatDate(item?.customFields?.scheduledDate, 'dd-MM-yyyy')
        : '-',
      startDate: item?.customFields?.startDate
        ? formatDate(item?.customFields?.startDate, LOGISTICS_DATE_FORMAT)
        : '-',
      endDate: item?.customFields?.endDate
        ? formatDate(item?.customFields?.endDate, LOGISTICS_DATE_FORMAT)
        : '-',
      timeAgo: item?.createdAt ? timeFromNow(item?.createdAt) : '-',
      preparation:
        LOGISTICS_FIELDS_MAPPER.preparation.options.find(
          (option) => option.value === item?.customFields?.preparation
        )?.text || '-',
      deliveryVehicle,
      returnVehicle,
      completedAt: item?.completedAt
        ? formatDate(item?.completedAt, LOGISTICS_DATE_FORMAT)
        : '-',
    };
  });
};

const getLogisticsSortingParams = (params) => {
  const {
    page = 1,
    pageSize = LOGISTICS_PAGE_SIZE,
    orderBy = LOGISTICS_DEFAULT_ORDER_BY,
    order = LOGISTICS_DEFAULT_ORDER,
    query,
  } = params;

  const sortingCustomFieldsMap = {
    [LOGISTICS_FIELDS_MAPPER.category.key]: 'customFields.category',
    [LOGISTICS_FIELDS_MAPPER.owner.key]: 'customFields.owner',
    [LOGISTICS_FIELDS_MAPPER.startDate.key]: 'customFields.startDate',
    [LOGISTICS_FIELDS_MAPPER.preparation.key]: 'customFields.preparation',
    [LOGISTICS_FIELDS_MAPPER.servicePoint.key]: 'customFields.servicePoint',
    [LOGISTICS_FIELDS_MAPPER.deliveryVehicle.key]:
      'customFields.deliveryVehicleBrand', // delivery vehicle has combined brand model so we sort by brand
    [LOGISTICS_FIELDS_MAPPER.returnVehicle.key]:
      'customFields.returnVehicleBrand', // return vehicle has combined brand model so we sort by brand
  };

  return getCleanedUrlParams({
    page,
    pageSize,
    orderBy:
      sortingCustomFieldsMap[orderBy] ||
      LOGISTICS_SORTING_KEYS[orderBy] ||
      orderBy,
    order,
    query,
  });
};

const getLogisticsPayload = (fields = {}, modalFilters = null) => {
  const { ...filters } = fields;

  const value = [
    {
      field: 'board_id',
      operator: FILTER_OPERATOR_ENUMS['='],
      type: FILTER_RULE_ENUMS.ComparableRuleType,
      value: BOARDS_ID.LOGISTICS,
    },
  ];

  if (filters) {
    Object.keys(filters)
      .filter((key) => !!LOGISTICS_FILTERS_KEYS[key])
      .forEach((key) => {
        if (
          LOGISTICS_FILTERS_KEYS[key] === LOGISTICS_FIELDS_MAPPER.assignee.key
        ) {
          // search for primaryDriver or secondaryDriver
          value.push({
            type: FILTER_RULE_ENUMS.OrRuleType,
            value: [
              LOGISTICS_FIELDS_MAPPER.primaryDriver.key,
              LOGISTICS_FIELDS_MAPPER.secondaryDriver.key,
            ].map((field) => ({
              type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
              operator: FILTER_OPERATOR_ENUMS['='],
              field: LOGISTICS_FILTERS_KEYS[field],
              value: getFieldValueByRuleType(field, filters[key]),
            })),
          });
        } else {
          // search for other fields
          value.push({
            field: LOGISTICS_FILTERS_KEYS[key],
            operator: FILTER_OPERATOR_ENUMS['='],
            type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
            value: getFieldValueByRuleType(key, filters[key]),
          });
        }
      });
  }
  value.push(...(modalFilters?.value || []));

  return {
    field: null,
    operator: null,
    type: FILTER_RULE_ENUMS.AndRuleType,
    value,
  };
};

const handleCurrentLocations = (data = []) =>
  data
    ?.map((item) => {
      return {
        text: item?.name,
        value: item?.id,
      };
    })
    .sort((a, b) => a.text.localeCompare(b.text));

const handleServicePoints = (servicePointsWithDefaultLocations = []) => {
  const options = [
    ...servicePointsWithDefaultLocations,
    {
      text: 'Athens International Airport "Eleftherios Venizelos"',
      value: 'Athens International Airport "Eleftherios Venizelos"',
    },
  ];

  return options.sort((a, b) => a.text.localeCompare(b.text));
};

const handleLogisticTask = ({
  data = {},
  assignees = [],
  servicePoints = [],
  currentLocations = [],
}) => {
  const primaryDriver = data?.customFields?.primaryDriver
    ? assignees.find((item) => item.value === data?.customFields?.primaryDriver)
    : '';
  const secondaryDriver = data?.customFields?.secondaryDriver
    ? assignees.find(
        (item) => item.value === data?.customFields?.secondaryDriver
      )
    : '';
  const preparation = data?.customFields?.preparation
    ? LOGISTICS_FIELDS_MAPPER.preparation.options.find(
        (item) => item.value === data?.customFields?.preparation
      )
    : '';
  const pickupLocation = data?.customFields?.pickupLocation
    ? servicePoints.find(
        (item) => item.value === data?.customFields?.pickupLocation
      )
    : '';
  const dropoffLocation = data?.customFields?.dropoffLocation
    ? servicePoints.find(
        (item) => item.value === data?.customFields?.dropoffLocation
      )
    : '';
  const currentLocation = data?.customFields?.currentLocation
    ? currentLocations.find(
        (item) => item.value === data?.customFields?.currentLocation
      )
    : '';
  const task = {
    ...data,
    customFields: {
      ...data.customFields,
      primaryDriver,
      secondaryDriver,
      preparation,
      pickupLocation,
      dropoffLocation,
      currentLocation,
    },
    comments: sortArray(data?.comments || [], 'createdAt', 'desc')?.map(
      (entry) => {
        return {
          id: entry?.id,
          date: entry?.createdAt
            ? formatDate(entry?.createdAt, 'dd MMMM yyyy')
            : '-',
          time: entry?.createdAt ? formatDate(entry?.createdAt, 'HH:mm') : '-',
          name: entry?.userFullName || entry?.user || '-',
          comment: entry?.comment,
          edit: entry?.editedAt
            ? formatDate(entry?.editedAt, 'dd MMMM yyyy HH:mm')
            : '',
        };
      }
    ),
    tags: data?.tags?.map((tag) => {
      return LOGISTICS_FIELDS_MAPPER.tags.options.find(
        (option) => option.value === tag
      );
    }),
  };
  return task;
};

export {
  getLogisticsSortingParams,
  getLogisticsPayload,
  handleLogisticsData,
  handleServicePoints,
  handleLogisticTask,
  handleCurrentLocations,
};
